import { useObserver } from 'mobx-react-lite';
import React from 'react';
import SlidingPane from 'react-sliding-pane';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import styled from 'styled-components/macro';
import removeDoubleSlashes from '../utils/removeDoubleSlashes';
import IconButton from './IconButton';
import ProductCardV2 from './ProductCardV2';
import { useAddressSelectStore } from '../models/AddressSelect';
import { useCartStore } from '../models/Cart';
import AddressContainer from './AddressContainer';
import Button from './Button';
import SpinnerV2 from '../icons/SpinnerV2';
import { formatCurrency } from '../utils/formatCurrencyWithoutDecimals';
import { CartActionOption } from '../types/CartItemProps';
import { CartProps } from '../types/CartProps';

const emptyCartImageUrl = removeDoubleSlashes(
  `${(globalThis as any)['BASE_URL']}/assets/empty-cart.png`,
);

const UnstyledCartV2: React.FC<CartProps> = ({ className, ...props }) => {
  const addressSelectStore = useAddressSelectStore();
  const state = useCartStore();

  const calculatingPriceCssClass = state.calculatePriceState
    ? ' ww-cart-' + state.calculatePriceState
    : '';

  return useObserver(() => (
    <SlidingPane
      overlayClassName="z-top"
      className={
        `ww-widget ww-cart flex w-full max-w-screen-sm flex-col ${calculatingPriceCssClass} ` +
        className
      }
      shouldCloseOnEsc={true}
      closeIcon={
        <IconButton aria-label="close">
          <i className="icon-cancel" />
        </IconButton>
      }
      isOpen={state.isOpen}
      title="Your Cart"
      from="right"
      width="100%"
      onRequestClose={state.hide}
    >
      {state.items.length > 0 ? (
        <div className="absolute inset-0 flex flex-col">
          <div className="cart-items flex-basis-40 flex-grow overflow-auto">
            {state.items.map((val) => (
              <div key={val.id} className="my-5 mx-3 flex flex-col sm:mx-10">
                <ProductCardV2
                  key={val.id}
                  product={{ id: val.id.toString() }}
                  headerIcons="remove"
                  enablevariations={false}
                  className="flex-grow"
                  cartaction={CartActionOption.None}
                  toasteroptions={{}}
                  priceFormat={{
                    display: 'override|minimumUnitPrice',
                    override:
                      (val.price *
                        (val.activeLabel?.perQuantity ??
                          val.minimumUnitPurchase ??
                          1)) /
                      val.cartQuantity,
                    suffix: val.activeLabel
                      ? `${
                          val.activeLabel.perQuantity > 1
                            ? val.activeLabel.perQuantity
                            : ''
                        }${val.activeLabel.suffixText.replace('per', '')}${
                          val.isVariation
                            ? ' - ' + (val.variationTypeDescription ?? '')
                            : ''
                        }`
                      : undefined,
                  }}
                />
                <div className="mt-3 text-right text-sm">
                  <span className="ww-cart-item-total">
                    {val.value > 0 &&
                      `${formatCurrency(val.price, state.currencyCode, {
                        hideZeroDecimals: true,
                      })}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="footer flex-basis-auto flex flex-grow-0 flex-col justify-end text-base">
            {state.errorMessage !== '' &&
            state.errorMessage !== undefined &&
            state.errorMessage !== null ? (
              <div
                className={'callout mx-3 my-5 sm:mx-10 ' + state.errorCssClass}
              >
                <span>{state.errorMessage}</span>
              </div>
            ) : (
              <></>
            )}

            {addressSelectStore.hasAddressFieldSelection && (
              <div className="flex flex-col border-t border-solid border-gray-200 px-3 py-5 text-base sm:px-10">
                <label>Shipping to</label>
                <AddressContainer
                  selectmessage="Select location for shipping"
                  version={2}
                  className="text-base"
                />
              </div>
            )}
            <div className="shipping-total border-t border-solid border-gray-200 px-3 py-5 sm:px-10">
              <div className="flex flex-row">
                <label className="flex-1 text-left">Shipping</label>
                <div className="ww-cart-total text-right">
                  {typeof state.shippingFee === 'number'
                    ? `${formatCurrency(state.shippingFee, state.currencyCode, {
                        hideZeroDecimals: true,
                      })}`
                    : '$-.--'}
                </div>
              </div>

              {!state.errorMessage &&
                state.totalItemCount > 0 &&
                state.totalTaxAdded !== 0 && (
                  <div className="flex flex-row">
                    <label className="flex-1 text-left">
                      {state.addedTaxCodeDescription}
                    </label>
                    <div className="ww-cart-total text-right">
                      {`${formatCurrency(
                        state.totalTaxAdded,
                        state.currencyCode,
                        { hideZeroDecimals: true },
                      )}`}
                    </div>
                  </div>
                )}

              <div style={{ display: 'flex', width: '100%' }}>
                <label className="flex-1 text-left font-bold">Total</label>
                <div className="ww-cart-total text-right font-bold">
                  {formatCurrency(state.totalPrice, state.currencyCode, {
                    hideZeroDecimals: true,
                  })}
                  {typeof state.shippingFee === 'number' ? (
                    <></>
                  ) : (
                    <span className="amount-addon"> + shipping</span>
                  )}
                </div>
              </div>
            </div>
            <Button
              className="check-out-button btn-v2 relative mx-3 my-5 text-sm sm:mx-10"
              onClick={(e) => {
                e.preventDefault();
                state.checkout();
              }}
              disabled={!state.ableToCheckout}
            >
              Checkout
              {state.calculatePriceState === 'pending' ? (
                <SpinnerV2 className="absolute right-2" />
              ) : (
                <></>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="empty-cart-container absolute inset-0 text-center">
          <img
            className="empty-cart-img"
            src={props.emptyimageurl || emptyCartImageUrl}
            alt={'empty cart'}
          />
          <div className="text-container">
            <span className="line-height-30 text block text-xl font-bold">
              {props.emptytitle || 'Uh oh, your cart is empty!'}
            </span>
            <span className="text text-base font-light">
              {props.emptysubtitle || 'Let us help with that…'}
            </span>
          </div>
          <Button
            className="shop-now-button btn-v2"
            onClick={(e) => {
              e.preventDefault();
              state.hide();
            }}
          >
            {props.emptybuttontext || 'Shop Now'}
          </Button>
        </div>
      )}
    </SlidingPane>
  ));
};

const CartV2 = styled(UnstyledCartV2)`
  .empty-cart-container {
    margin: auto;
    height: fit-content;
  }

  .shop-now-button {
    width: 400px;
    height: 50px;
    fontsize: 12px;
    margin: auto;
    max-width: 100%;
  }

  .empty-cart-img {
    width: 250px;
    height: 250px;
    margin: auto;
    max-width: 100%;
  }

  .text-container {
    margin: 30px 0 50px 0;
  }

  .callout {
    position: relative;
    width: auto;
    padding: 18px;
    background-color: rgb(194, 155, 71);
    border-radius: 1.231em;
    color: white;
    font-size: 14px;
  }

  .callout:before {
    content: '';
    position: absolute;
    width: 0px;
    height: 0px;
    right: 20px;
    bottom: -35px;
    border-width: 20px 20px 25px;
    border-style: solid;
    border-color: rgb(194, 155, 71) transparent transparent;
    border-image: initial;
  }

  .ww-product-card {
    .ww-product-card-header {
      .ww-icon-button {
        font-size: 16px;
        border: unset;
        color: black;
        background: white;
      }
    }
  }

  &.ww-cart-pending .ww-product-card-footer .price-info,
  &.ww-cart-pending .ww-product-card-footer .ww-price-suffix,
  &.ww-cart-pending .ww-cart-total,
  &.ww-cart-pending .ww-cart-item-total {
    color: var(--gray-1);
    background-color: var(--gray-1);
  }

  .amount-addon {
    font-style: italic;
  }
`;

export default CartV2;
